import {
  arrow,
  autoPlacement,
  autoUpdate,
  offset,
  size,
  type OffsetOptions,
  type SizeOptions,
  safePolygon,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useTransitionStyles,
} from "@floating-ui/react";
import React, { useRef, useState } from "react";

import { cn } from "@/utils/cn";

import Button from "../button/button";
import Portal from "../portal/portal";

interface PopoverProps {
  children: React.ReactElement<any, string | React.JSXElementConstructor<any>>;
  /** The content of the popover */
  content: React.ReactNode;
  /** Should we remove padding on the Popover */
  isFlush?: boolean;
  /** Should the popover trigger via click or hover */
  interaction?: "click" | "hover";
  /** Add a class to the reference (trigger) element */
  referenceElementClassName?: string;
  containerClassName?: string;
  zIndex?: number;
  dismissText?: string;
  /** Function to call when opening */
  onOpen?(): void;
  isOpen?: boolean;
  onOpenChange?(open: boolean): void;
  placement?: "top" | "bottom" | "top-start" | "bottom-start" | "bottom-end";
  offsetOptions?: OffsetOptions;
  sizeOptions?: SizeOptions;
}

/**
 * Popover
 *
 * Similar to tooltip, but requires a click and is used for larger blocks
 * of information.
 */
const Popover: React.FC<PopoverProps> = (props) => {
  const {
    children,
    content,
    dismissText,
    referenceElementClassName,
    containerClassName,
    onOpen,
    interaction = "hover",
    isFlush = false,
    zIndex,
    isOpen: controlledIsOpen,
    onOpenChange,
    placement = "top",
    offsetOptions = 10,
    sizeOptions,
  } = props;

  const [internalIsOpen, setInternalIsOpen] = useState<boolean>(false);

  // Use isOpen prop if provided, otherwise use internal state
  const isOpen = controlledIsOpen ?? internalIsOpen;

  const handleOpenChange = (value: boolean) => {
    onOpenChange?.(value);

    if (controlledIsOpen === undefined) {
      setInternalIsOpen(value);
    }

    if (value && onOpen) {
      onOpen();
    }
  };

  const arrowRef = useRef<SVGSVGElement>(null);

  const { x, y, strategy, refs, context } = useFloating({
    open: isOpen,
    onOpenChange: handleOpenChange,
    whileElementsMounted: autoUpdate,
    placement: placement,
    middleware: [
      autoPlacement({
        allowedPlacements: [
          "top",
          "bottom",
          "top-start",
          "bottom-start",
          "bottom-end",
        ],
      }),
      offset(offsetOptions),
      arrow({
        element: arrowRef,
      }),
      ...(sizeOptions ? [size(sizeOptions)] : []),
    ],
  });

  const { isMounted, styles } = useTransitionStyles(context, {
    initial: {
      opacity: 0,
      transform: "scale(0.8)",
    },
    duration: {
      open: 200,
      close: 200,
    },
  });

  const click = useClick(context, { enabled: interaction === "click" });
  const hover = useHover(context, {
    enabled: interaction === "hover",
    handleClose: safePolygon(),
    delay: {
      open: 300,
      close: 0,
    },
  });
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    hover,
    dismiss,
  ]);

  const handleClose = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleOpenChange(false);
  };

  return (
    <>
      {React.cloneElement(children, {
        ref: refs.setReference,
        ...getReferenceProps({
          onClick(event) {
            event.preventDefault();
            event.stopPropagation();
          },
        }),
        className: cn(children.props.className, referenceElementClassName),
      })}

      {isMounted && (
        <Portal>
          <div
            ref={refs.setFloating}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
              ...styles,
              // Prefer inline z-index; dynamic Tailwind `z-[${n}]` is not JIT-safe.
              zIndex: zIndex ?? 50,
            }}
            className={cn(
              "overflow-hidden rounded-lg bg-white shadow-2xl dark:bg-gray-900 dark:ring-2 dark:ring-gray-800",
              {
                "p-4": !isFlush,
              },
              containerClassName,
            )}
            {...getFloatingProps()}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="false"
          >
            {content}

            {dismissText && (
              <Button
                className="mt-4 w-full"
                variant="outline"
                onClick={handleClose}
              >
                {dismissText}
              </Button>
            )}
          </div>
        </Portal>
      )}
    </>
  );
};

export { Popover as default, Popover, type PopoverProps };
